/**
 * The asset's head card: kind, status, maturity and home country as badges and words; industries, partners and
 * countries; the experts with name and e-mail (and "not signed in yet, expires on <date>" for a provisioned one); and
 * the freshness line ("Confirmed 3 months ago", a Stale badge after six months).
 */

import { useCountries } from '@/api/queries';
import type { AssetOut } from '@/api/types';
import { Badge } from '@/ui/Badge';

import { confirmedText, countryName, expertNote } from '../assetUi';

function TagLine({ label, names }: { label: string; names: string[] }) {
  return (
    <div className="asset-fact">
      <dt>{label}</dt>
      <dd>{names.length > 0 ? names.join(', ') : 'None'}</dd>
    </div>
  );
}

function Experts({ asset }: { asset: AssetOut }) {
  const experts = [...asset.experts].sort((a, b) => a.position - b.position);
  return (
    <div className="asset-fact asset-fact-wide">
      <dt>Experts</dt>
      <dd>
        {experts.length === 0 ? (
          'None yet'
        ) : (
          <ul className="asset-experts">
            {experts.map((e) => {
              const note = expertNote(e);
              return (
                <li key={e.user_id}>
                  <b>{e.display_name}</b>
                  {e.label && <span className="hint"> ({e.label})</span>} <a href={`mailto:${e.email}`}>{e.email}</a>
                  {note && <span className="hint">, {note}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </dd>
    </div>
  );
}

export function AssetFacts({ asset }: { asset: AssetOut }) {
  const countries = useCountries();
  const confirmedBy = asset.freshness.last_confirmed_by?.display_name;
  return (
    <div className="card asset-facts">
      <div className="asset-facts-badges">
        <Badge variant="kind" value={asset.kind} />
        <Badge variant="status" value={asset.status} />
        <Badge variant="maturity" value={asset.maturity} />
        <span className="asset-home">Home country: {countryName(asset.home_country, countries.data)}</span>
        <span className="asset-freshness">
          {confirmedText(asset.freshness.last_confirmed_at)}
          {confirmedBy && <span className="hint"> by {confirmedBy}</span>}
        </span>
        {asset.freshness.stale && <Badge variant="freshness" value="stale" title="Last confirmed more than six months ago" />}
      </div>
      {asset.summary && <p className="asset-summary">{asset.summary}</p>}
      <dl className="asset-fact-list">
        <TagLine label="Industries" names={asset.industries.map((i) => i.name)} />
        <TagLine label="Partners" names={asset.partners.map((p) => p.name)} />
        <TagLine label="Available in" names={asset.countries.map((c) => c.name)} />
        <Experts asset={asset} />
      </dl>
    </div>
  );
}
